import React, { FunctionComponent, useState } from 'react'
import { useIntl } from 'react-intl'
import { Pressable, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { InfoPopoverTriggerAction, RIGHT_TO_LEFT_LANGUAGES, useLyraIntl } from '@lyrahealth-inc/shared-app-logic'
import { LYRA_DISPLAY_LANGUAGES, sortDisplayLanguages } from '@lyrahealth-inc/shared-app-logic/src/features/intl'

import { LanguageSelectorModal } from './LanguageSelectorModal'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { GlobeIcon } from '../../atoms/icons/GlobeIcon'
import { InfoPopover } from '../../atoms/infoPopover/InfoPopover'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { RightToLeftLanguageAlertModal } from '../rightToLeftLanguageAlertModal/RightToLeftLanguageAlertModal'

export interface LanguageSelectorProps {
  style?: ViewStyle
  customTextColor?: string
  /** Dropdown alignment - left or right */
  dropdownAlignment?: 'left' | 'right'
  beforeLaunch?: boolean
  testID?: string
  onLanguageChange?: (language: string) => void
  onGetHelpPress?: () => void
}

const LanguageSelectorContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const LanguageSelectorButton = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: 0,
  margin: theme.breakpoints.isMobileSized ? 0 : `0 ${theme.spacing['24px']}`,
  minWidth: 'auto',
  backgroundColor: 'transparent',
  borderWidth: 0,
}))

const GlobeIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.breakpoints.isMinWidthTablet ? theme.spacing['8px'] : 0,
}))

const ChevronIconContainer = styled.View<{
  theme: ThemeType
}>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
}))

const LanguageText = styled(BodyText)<{
  textColor?: string
}>(({ textColor }: any) => ({
  color: textColor,
  textDecorationLine: 'underline',
}))

export const LanguageSelector: FunctionComponent<LanguageSelectorProps> = ({
  style,
  customTextColor,
  dropdownAlignment = 'right',
  beforeLaunch = false,
  testID,
  onLanguageChange,
  onGetHelpPress,
}) => {
  const { formatMessage } = useIntl()
  const { colors, breakpoints } = useTheme()
  const { isMobileSized } = breakpoints
  const textColor = customTextColor || colors.linkDefault
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { activeLanguage, setLanguage, availableLanguages: defaultAvailableLanguages } = useLyraIntl()
  const [rightToLeftLanguageAlertModalState, setRightToLeftLanguageAlertModalState] = useState({
    selectedLocale: '',
    isModalOpened: false,
  })

  const onLanguageSelectorModalClick = (selectedLocale: string) => {
    if (RIGHT_TO_LEFT_LANGUAGES.hasOwnProperty(selectedLocale)) {
      setRightToLeftLanguageAlertModalState({
        selectedLocale,
        isModalOpened: true,
      })
      return
    } else if (selectedLocale !== activeLanguage) {
      setLanguage(selectedLocale)
      onLanguageChange?.(selectedLocale)
    }
    setIsPopoverOpen(false)
  }

  const currentLanguage = LYRA_DISPLAY_LANGUAGES[activeLanguage as keyof typeof LYRA_DISPLAY_LANGUAGES]?.name

  const availableLanguages: string[] = [...defaultAvailableLanguages]
  /**
   * Adding the right to left languages directly to the language selector because adding them to the config
   * would also add matching and selecting based on the users browser default language setting
   * and this would also load translation files which do not exist for RTL languages.
   *
   * Disabling Coming Soon RTL languages for the selector when Pathways is enabled because Bukwild has not supported
   * the new look and feel of pathways for RTL languages yet.
   */
  availableLanguages.push(...Object.keys(beforeLaunch ? {} : RIGHT_TO_LEFT_LANGUAGES))

  const handleShowClassicVersionPress = () => {
    if (rightToLeftLanguageAlertModalState.selectedLocale) {
      if (IS_WEB) {
        window.location.href = `/${
          RIGHT_TO_LEFT_LANGUAGES[
            rightToLeftLanguageAlertModalState.selectedLocale as keyof typeof RIGHT_TO_LEFT_LANGUAGES
          ].micrositePath
        }`
      }
    }
  }

  return (
    <>
      <LanguageSelectorContainer testID={testID || tID('LanguageSelection-button')} style={style}>
        <InfoPopover
          placement={dropdownAlignment === 'right' ? 'bottom right' : 'bottom left'}
          offset={24}
          content={
            <LanguageSelectorModal
              activeLanguage={activeLanguage}
              availableLanguages={sortDisplayLanguages(availableLanguages)}
              onClick={onLanguageSelectorModalClick}
              beforeLaunch={beforeLaunch}
              onGetHelpPress={onGetHelpPress}
            />
          }
          trigger={
            <LanguageSelectorButton
              testID={tID('LanguageSelector-button')}
              accessibilityRole='button'
              accessibilityLabel={formatMessage({
                defaultMessage: 'Display language selector',
                description: 'Screen reader text for the display language selector',
              })}
            >
              <GlobeIconContainer>
                <GlobeIcon color={textColor} fillColor={textColor} />
              </GlobeIconContainer>
              {!isMobileSized && (
                <LanguageText testID={tID('LanguageSelector-language')} textColor={textColor}>
                  {currentLanguage}
                </LanguageText>
              )}
              <ChevronIconContainer>
                <ChevronIcon
                  fillColor={textColor}
                  direction={isPopoverOpen ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
                />
              </ChevronIconContainer>
            </LanguageSelectorButton>
          }
          popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
          shouldDisplayCloseButton={false}
          isPopoverOpen={isPopoverOpen}
          setIsPopoverOpen={setIsPopoverOpen}
          popoverStyle={{
            container: {
              maxWidth: '600px',
              width: 'auto',
              padding: '0px',
              ...(isMobileSized && {
                position: 'fixed',
                top: '62px',
                left: '8px',
                right: '8px',
                bottom: '8px',
                maxHeight: '100vh',
                maxWidth: '100%',
                overflowY: 'scroll',
              }),
            },
          }}
        />
      </LanguageSelectorContainer>
      {rightToLeftLanguageAlertModalState.isModalOpened && (
        <RightToLeftLanguageAlertModal
          selectedLocale={rightToLeftLanguageAlertModalState.selectedLocale!}
          onShowClassicVersionPress={handleShowClassicVersionPress}
          onUpdateLanguagePress={() =>
            setRightToLeftLanguageAlertModalState({ selectedLocale: '', isModalOpened: false })
          }
          visible={rightToLeftLanguageAlertModalState.isModalOpened}
        />
      )}
    </>
  )
}
